// Context
export type JwtPayload = {
	email?: string;
	"cognito:groups"?: string[];
	exp?: number;
	// add other custom claims here
};

export type UserContextValue = {
	user: () => JwtPayload | null;
	refresh: () => void;
	logout: () => void;
	userIs: (group: string) => boolean;
	isLoggedIn: () => boolean;
	impersontation: object;
	setImpersontation: (id: number) => void;
};

// SCHEMES
export type RIASScheme = {
	schemeNumber: string;
	fullName: string;
	name: string;
	joiningFee: number;
	joiningFeeRIASMember?: number;
	renewalFee?: number;
	slug: string;
	shortName: string;
	prefix: string;
	// Set on CPD membership in schemes.json: a one-off joining fee with no
	// renewal, no Approved Body and no certificates.
	notScheme?: boolean;
};

export type RIASSchemes = Record<string, RIASScheme>;
export type RIASSchemeKey = keyof RIASSchemes;

// ASSESSMENT MODULES
export type AssessmentModule = {
	[key: string]: string | undefined;
};

export interface AssessmentData {
	s6Domestic: AssessmentModule[];
	s6NonDomestic: AssessmentModule[];
}
export type AllAssessmentModules = Record<string, AssessmentData>;
export type AssessmentModules = Record<string, AssessmentModule[]>;

// QUESTIONS
export type Question = {
	question: string;
	answers: string[];
	correct: number; // index in `answers`
};
export type AssessmentQuestions = {
	name: string;
	questions: Question[];
};
export type AssessmentQuestionsData = Record<string, AssessmentQuestions>;

export type AllowedUser = {
	sub: string;
	"cognito:groups"?: string[];
	email_verified?: boolean;
	phone_number_verified?: boolean;
	"cognito:username"?: string;
	phone_number?: string;
	exp: number;
	email?: string;
};

// Module augmentation for SolidStart
declare module "@solidjs/start/server" {
	interface Locals {
		user?: AllowedUser;
	}
}

export type RIASApp = {
	impersontation: object;
	setImpersontation: (id: number) => void;
};

export type Address = {
	name: string;
	addr1: string | null;
	addr2: string | null;
	town: string | null;
	county: string | null;

	postcode: string | null;
};

export type RIASProject = {
	name: string;
	reference: string;
	description: string;
	localAuthority: string;
	site: Address;
	agent: Address;
	applicant: Address;
	plots: [];
	recordType: string;
	certificateCount: number;
	created: number;
	status: string;
};

export type Project = {
	plots: any[];
	localAuthority: string;
	certificateCount: number;
	status: string;
	created: number;
	site: Address;
	name: string;
	reference: string;
	applicant: Address;
	agent: Address;
	description: string;
	recordType: "PROJECT";
	pk?: string;
	sk?: string;
};

export type Certificate = {
	meta: {
		amendments: string[];
		buildingWarrant: string;
		complianceMethods: string;
		coordindator: string;
		descriptionOfWorks: string;
		isStagedCertificate: boolean;
		plots: number[];
		schedule1Attached: string;
		sections: {
			energy: string;
			environment: string[];
			fire: string[];
			noise: string;
			safety: string[];
			structure: string;
		};
		stages: string[];
		typeOfWorks: string;
		worksValue: number;
	};
	invoiceId?: string;
	cost: number;
	created: string | null;
	paid?: number;
	name: string | null;
	parent: string | null;
	recordType: "CERTIFICATE";
	riasId: number | null;
	scheme: string;
	status: string;
	subscriptionId: string | null;

	pk?: string;
	sk?: string;
};

export type PendingApprovedBody = {
	address: Record<string, string>;
	coordinator: { name: string; email: string; telephone: string };
	deputy: { name: string; email: string; telephone: string };
	submitted: number;
};

// A stored APPROVEDBODY record - the submitted details plus DynamoDB keys and
// the optional display name set on legacy/admin-edited bodies.
export type ApprovedBody = Omit<PendingApprovedBody, "submitted"> & {
	pk?: string;
	sk?: string;
	name?: string;
	status?: string;
	piRenewalDate?: number | null;
};

export type Subscription = {
	pk?: string;
	sk?: string;
	approvedBodyId?: string;
	expires: number;
	joined?: number;
	name: string;
	scheme: string;
	status: string;
	recordType: "SUBSCRIPTION";
	invoiceId?: string;
	created?: number;
	suspended?: number;
	terminated?: number;
	// Set when verification raises the invoice; the reminder cron counts from it.
	pendingSince?: number;
	// A member who withdrew their own application, rather than being struck off.
	cancelledBy?: string;
	cancelReason?: string;
	cancelComment?: string;
	cancelToken?: string;
	// Timestamps of the reminders already sent, so neither goes twice.
	remindedAt?: number;
	remindedStage?: number;
	pendingAB?: PendingApprovedBody | null;
};

export enum RIASRoles {
	VERIFYING = "verifying",
	REJECTED = "rejected",
	ADMIN = "administrator",
	CERTIFIER = "certifier",
	LAPSED = "lapsed",
	CPD = "cpd",
	AUDITOR = "auditor",
}

export enum RIASRecordType {
	APPROVEDBODY = "APPROVEDBODY",
	ASSESSMENT = "ASSESSMENT",
	SUBSCRIPTION = "SUBSCRIPTION",
	PROJECT = "PROJECT",
	CERTIFICATE = "CERTIFICATE",
	USER = "USER",
	MAX_ATTEMPTS = "ASSESSMENT_ATTEMPTS",
	SUSPENSION = "SUSPENSION",
}

export enum UserAction {
	LOGIN = "LOGIN",
	LOGOUT = "LOGOUT",
}

export enum AssessmentState {
	ABORTED = "aborted",
	ASSESSING = "in-progress",
	FAILED = "failed",
	PASSED = "passed",
}

export enum AssessmentAction {
	START = "START",
	END = "END",
	PASSED = "PASSED",
	FAILED = "FAILED",
	ABORTED = "ABORTED",
}
export enum SubscriptionAction {
	REGISTERED = "REGISTERED",
	VALIDATED = "VALIDATED",
	APPROVED_BODY_ADDED = "AB_ADDED",
	CREATED = "CREATED",
	RENEWED = "RENEWED",
	EXPIRED = "EXPIRED",
	TERMINATED = "TERMINATED",
	REMINDER7 = "REMINDED7",
	REMINDER14 = "REMINDED14",
}

export enum SubscriptionState {
	VALIDATING = "validating",
	APPLYING = "applying",
	PENDING = "payment-pending",
	ASSESSING = "assessing",
	PRACTICAL = "practical",
	REQUIRES_BODY = "requires-approved-body",
	PENDING_BODY = "pending-approved-body",
	ACTIVE = "active",
	RENEWING = "renewing",
	EXPIRED = "expired",
	SUSPENDED = "suspended",
	TERMINATED = "terminated",
	// An application the member withdrew themselves before paying. Distinct from
	// TERMINATED, which means struck off the register.
	CANCELLED = "cancelled",
	CPD = "cpd",
}

export enum CertificateState {
	DRAFT = "draft",
	PUBLISHED = "published",
	PAID = "paid",
	VOID = "void",
}

export enum ProjectAction {
	CREATED = "CREATED",
}

export enum CertificateAction {
	CREATED = "CREATED",
	PUBLISHED = "PUBLISHED",
	PAID = "PAID",
}

export type LogAction =
	| UserAction
	| AssessmentAction
	| SubscriptionAction
	| ProjectAction
	| CertificateAction;

export type AddressType = {
	title: string;
	addr1: string;
	addr2?: string;
	town?: string | null;
	county?: string | null;
	postcode: string;
	localAuthority?: string | null;
	telephone?: string | null;
};

export interface S3FilesType {
    folders: string[];
    files: Array<{
        key: string | undefined;
        size: number | undefined;
        modified: Date | undefined;
    }> | undefined;
}